/**
 * CenterOfMassIndicator - Visual indicator showing tower stability
 * Shows the center of mass during freeze mode to help players balance
 */

import Phaser from 'phaser';
import { Vector2 } from '../../utils/vectorMath';

/**
 * Visual indicator for the tower's center of mass
 */
export class CenterOfMassIndicator {
  private graphics: Phaser.GameObjects.Graphics;
  private warningThreshold: number;
  private lastCoM: Vector2 = { x: 0, y: 0 };
  private isVisible: boolean = false;

  /**
   * @param scene - The Phaser scene
   * @param warningThreshold - Distance from center before warning (pixels)
   */
  constructor(scene: Phaser.Scene, warningThreshold: number = 50) {
    this.warningThreshold = warningThreshold;
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(100);
    this.graphics.setVisible(false);
  }

  /**
   * Show the indicator
   */
  show(): void {
    this.isVisible = true;
    this.graphics.setVisible(true);
  }

  /**
   * Hide the indicator
   */
  hide(): void {
    this.isVisible = false;
    this.graphics.setVisible(false);
    this.graphics.clear();
  }

  /**
   * Update the indicator with current body positions
   * @param bodies - All physics bodies in the tower
   * @param platformCenterX - X position of platform center for stability check
   */
  update(bodies: MatterJS.BodyType[], platformCenterX: number): void {
    if (!this.isVisible || bodies.length === 0) return;

    // Calculate center of mass
    const com = this.calculateCenterOfMass(bodies);
    this.lastCoM = com;

    // Determine stability
    const distanceFromCenter = Math.abs(com.x - platformCenterX);
    const isStable = distanceFromCenter < this.warningThreshold;

    // Draw indicator
    this.draw(com, isStable, platformCenterX);
  }

  /**
   * Get the last calculated center of mass
   */
  getLastCoM(): Vector2 {
    return { ...this.lastCoM };
  }

  /**
   * Check if the indicator is currently visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Calculate the center of mass of all bodies
   */
  private calculateCenterOfMass(bodies: MatterJS.BodyType[]): Vector2 {
    let totalMass = 0;
    let weightedX = 0;
    let weightedY = 0;

    for (const body of bodies) {
      const mass = body.mass || 1;
      totalMass += mass;
      weightedX += body.position.x * mass;
      weightedY += body.position.y * mass;
    }

    if (totalMass === 0) {
      return { x: 0, y: 0 };
    }

    return {
      x: weightedX / totalMass,
      y: weightedY / totalMass,
    };
  }

  /**
   * Draw the indicator
   */
  private draw(com: Vector2, isStable: boolean, platformCenterX: number): void {
    this.graphics.clear();

    // Colors
    const stableColor = 0x4CAF50;   // Green
    const warningColor = 0xFF5722;  // Red-orange

    const color = isStable ? stableColor : warningColor;

    // Draw center line from platform (subtle)
    this.graphics.lineStyle(1, 0x636E72, 0.3);
    this.graphics.lineBetween(platformCenterX, com.y - 50, platformCenterX, com.y + 50);

    // Draw CoM dot
    this.graphics.fillStyle(color, 0.9);
    this.graphics.fillCircle(com.x, com.y, 6);

    // Draw inner highlight
    this.graphics.fillStyle(0xFFFFFF, 0.5);
    this.graphics.fillCircle(com.x - 1, com.y - 1, 2);

    // Draw warning ring if unstable
    if (!isStable) {
      this.graphics.lineStyle(2, warningColor, 0.6);
      this.graphics.strokeCircle(com.x, com.y, 12);

      // Draw offset indicator line
      this.graphics.lineStyle(1, warningColor, 0.4);
      this.graphics.lineBetween(platformCenterX, com.y, com.x, com.y);
    }
  }

  /**
   * Destroy the indicator
   */
  destroy(): void {
    this.graphics.destroy();
  }
}
